import React, { useState, Children } from 'react';
import { COATreeView } from './COATreeView';
import { SegmentEditor } from './SegmentEditor';
import { AccountTypeManager } from './AccountTypeManager';
import { COAToolbar } from './COAToolbar';
import { OrgUnitGLIntegration } from './OrgUnitGLIntegration';
import { COAScopeViewer } from './COAScopeViewer';
import { ChevronRightIcon, BookIcon } from 'lucide-react';
import { sampleOrgUnits, sampleAccountTypes, sampleCOAData, flattenOrgUnits, flattenCOASegments, type COASegment, type AccountType, type OrgUnit } from './sharedFinancialData';
// Re-export types for backward compatibility
export type { COASegment, AccountType, OrgUnit };
// Define types
export type AccountType = {
  id: string;
  name: string;
  description?: string;
  color: string;
};
export type COASegment = {
  id: string;
  segmentCode: string;
  segmentName: string;
  accountTypeId: string;
  parentId?: string;
  isActive: boolean;
  activeFrom?: string;
  activeTo?: string;
  orgUnitId?: string;
  fullGLPath: string;
  level: number;
  children?: COASegment[];
  // OrgUnit scope configuration
  scopeType?: 'global' | 'specific'; // global = all OrgUnits, specific = selected OrgUnits
  applicableOrgUnitIds?: string[]; // Only used when scopeType is 'specific'
};
export function ChartOfAccounts({
  locale
}) {
  const [view, setView] = useState<'coa' | 'account-types' | 'integration' | 'scope'>('coa');
  const [selectedSegment, setSelectedSegment] = useState<COASegment | null>(null);
  const [isEditing, setIsEditing] = useState(false);
  const [showAddRoot, setShowAddRoot] = useState(false);
  const [showAddChild, setShowAddChild] = useState(false);
  // Use shared data sources
  const [accountTypes, setAccountTypes] = useState<AccountType[]>(sampleAccountTypes);
  const [coaData, setCoaData] = useState<COASegment[]>(sampleCOAData);
  const orgUnits = flattenOrgUnits(sampleOrgUnits);
  // Flatten COA data for integration view
  const flattenCOAData = (segments: COASegment[]): COASegment[] => {
    const result: COASegment[] = [];
    const flatten = (segs: COASegment[]) => {
      segs.forEach(seg => {
        result.push(seg);
        if (seg.children && seg.children.length > 0) {
          flatten(seg.children);
        }
      });
    };
    flatten(segments);
    return result;
  };
  // Function to handle segment selection
  const handleSelectSegment = (segment: COASegment) => {
    setSelectedSegment(segment);
    setIsEditing(false);
  };
  // Function to handle editing a segment
  const handleEditSegment = () => {
    if (selectedSegment) {
      setIsEditing(true);
    }
  };
  // Function to add a root segment
  const handleAddRootSegment = () => {
    setSelectedSegment(null);
    setShowAddRoot(true);
    setIsEditing(true);
  };
  // Function to add a child segment
  const handleAddChildSegment = () => {
    if (selectedSegment) {
      setShowAddChild(true);
      setIsEditing(true);
    }
  };
  // Function to save a segment (create or update)
  const handleSaveSegment = (segment: COASegment) => {
    // Generate a new ID for new segments
    const isNew = !segment.id;
    const segmentToSave = isNew ? {
      ...segment,
      id: `coa-${new Date().getTime()}`
    } : segment;
    // If it's a new root segment
    if (isNew && !segment.parentId) {
      setCoaData([...coaData, {
        ...segmentToSave,
        children: []
      }]);
    }
    // If it's a new child segment
    else if (isNew && segment.parentId) {
      // Find the parent and add the child
      const updatedData = addChildToParent(coaData, segment.parentId, {
        ...segmentToSave,
        children: []
      });
      setCoaData(updatedData);
    }
    // If it's an update to an existing segment
    else {
      const updatedData = updateSegmentInTree(coaData, segmentToSave);
      setCoaData(updatedData);
    }
    setIsEditing(false);
    setShowAddRoot(false);
    setShowAddChild(false);
    setSelectedSegment(segmentToSave);
  };
  // Helper function to add a child to a parent in the tree
  const addChildToParent = (segments: COASegment[], parentId: string, newChild: COASegment): COASegment[] => {
    return segments.map(segment => {
      if (segment.id === parentId) {
        return {
          ...segment,
          children: [...(segment.children || []), newChild]
        };
      }
      if (segment.children && segment.children.length > 0) {
        return {
          ...segment,
          children: addChildToParent(segment.children, parentId, newChild)
        };
      }
      return segment;
    });
  };
  // Helper function to update a segment in the tree
  const updateSegmentInTree = (segments: COASegment[], updatedSegment: COASegment): COASegment[] => {
    return segments.map(segment => {
      if (segment.id === updatedSegment.id) {
        return {
          ...updatedSegment,
          children: segment.children
        };
      }
      if (segment.children && segment.children.length > 0) {
        return {
          ...segment,
          children: updateSegmentInTree(segment.children, updatedSegment)
        };
      }
      return segment;
    });
  };
  // Function to delete a segment
  const handleDeleteSegment = (segmentId: string) => {
    // Only allow deletion if the segment has no children
    const canDelete = !hasChildren(coaData, segmentId);
    if (!canDelete) {
      alert('Cannot delete a segment with children. Remove all child segments first.');
      return;
    }
    const updatedData = deleteSegmentFromTree(coaData, segmentId);
    setCoaData(updatedData);
    if (selectedSegment && selectedSegment.id === segmentId) {
      setSelectedSegment(null);
      setIsEditing(false);
    }
  };
  // Helper function to check if a segment has children
  const hasChildren = (segments: COASegment[], segmentId: string): boolean => {
    for (const segment of segments) {
      if (segment.id === segmentId) {
        return segment.children && segment.children.length > 0;
      }
      if (segment.children && segment.children.length > 0) {
        if (hasChildren(segment.children, segmentId)) {
          return true;
        }
      }
    }
    return false;
  };
  // Helper function to delete a segment from the tree
  const deleteSegmentFromTree = (segments: COASegment[], segmentId: string): COASegment[] => {
    return segments.filter(segment => segment.id !== segmentId).map(segment => {
      if (segment.children && segment.children.length > 0) {
        return {
          ...segment,
          children: deleteSegmentFromTree(segment.children, segmentId)
        };
      }
      return segment;
    });
  };
  // Function to recalculate all paths
  const handleRecalculatePaths = () => {
    const updatedData = recalculateAllPaths(coaData);
    setCoaData(updatedData);
  };
  // Helper function to recalculate paths for all segments
  const recalculateAllPaths = (segments: COASegment[], parentPath: string = ''): COASegment[] => {
    return segments.map(segment => {
      const newPath = parentPath ? `${parentPath}.${segment.segmentCode}` : segment.segmentCode;
      const updatedSegment = {
        ...segment,
        fullGLPath: newPath
      };
      if (segment.children && segment.children.length > 0) {
        return {
          ...updatedSegment,
          children: recalculateAllPaths(segment.children, newPath)
        };
      }
      return updatedSegment;
    });
  };
  // Function to handle saving account types
  const handleSaveAccountType = (accountType: AccountType) => {
    const isNew = !accountType.id;
    if (isNew) {
      const newAccountType = {
        ...accountType,
        id: `at-${new Date().getTime()}`
      };
      setAccountTypes([...accountTypes, newAccountType]);
    } else {
      setAccountTypes(accountTypes.map(at => at.id === accountType.id ? accountType : at));
    }
  };
  // Function to handle deleting account types
  const handleDeleteAccountType = (accountTypeId: string) => {
    // Check if the account type is in use
    const isInUse = coaData.some(segment => segment.accountTypeId === accountTypeId || segment.children && segment.children.some(child => isAccountTypeUsed(child, accountTypeId)));
    if (isInUse) {
      alert('Cannot delete an account type that is in use. Remove all segments using this type first.');
      return;
    }
    setAccountTypes(accountTypes.filter(at => at.id !== accountTypeId));
  };
  // Helper function to check if an account type is used in a segment or its children
  const isAccountTypeUsed = (segment: COASegment, accountTypeId: string): boolean => {
    if (segment.accountTypeId === accountTypeId) {
      return true;
    }
    if (segment.children && segment.children.length > 0) {
      return segment.children.some(child => isAccountTypeUsed(child, accountTypeId));
    }
    return false;
  };
  // Render the appropriate view
  return <div className="space-y-6" data-id="element-8946">
      <div className="mb-8" data-id="element-8947">
        <h2 className="text-2xl font-bold text-gray-900" data-id="element-8948">
          {view === 'coa' ? 'Chart of Accounts' : 'Account Types Management'}
        </h2>
        <p className="mt-1 text-sm text-gray-500" data-id="element-8949">
          {view === 'coa' ? "Manage your organization's hierarchical chart of accounts structure" : 'Manage account types used in your chart of accounts'}
        </p>
      </div>

      {/* Tab Selector */}
      <div className="border-b border-gray-200 mb-6" data-id="element-8950">
        <nav className="-mb-px flex space-x-8" aria-label="Tabs" data-id="element-8951">
          <button onClick={() => setView('coa')} className={`${view === 'coa' ? 'border-indigo-500 text-indigo-600' : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'} whitespace-nowrap py-4 px-1 border-b-2 font-medium text-sm`} data-id="element-8952">
            Chart of Accounts
          </button>
          <button onClick={() => setView('scope')} className={`${view === 'scope' ? 'border-indigo-500 text-indigo-600' : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'} whitespace-nowrap py-4 px-1 border-b-2 font-medium text-sm`} data-id="element-8953">
            OrgUnit Scope
          </button>
          <button onClick={() => setView('integration')} className={`${view === 'integration' ? 'border-indigo-500 text-indigo-600' : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'} whitespace-nowrap py-4 px-1 border-b-2 font-medium text-sm`} data-id="element-8954">
            Integration Demo
          </button>
          <button onClick={() => setView('account-types')} className={`${view === 'account-types' ? 'border-indigo-500 text-indigo-600' : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'} whitespace-nowrap py-4 px-1 border-b-2 font-medium text-sm`} data-id="element-8955">
            Account Types
          </button>
        </nav>
      </div>
      {/* Chart of Accounts View */}
      {view === 'coa' && <div className="grid grid-cols-1 lg:grid-cols-3 gap-6" data-id="element-8956">
          <div className="lg:col-span-2" data-id="element-8957">
            <COAToolbar onAddRoot={handleAddRootSegment} onAddChild={handleAddChildSegment} onEdit={handleEditSegment} onDelete={selectedSegment ? () => handleDeleteSegment(selectedSegment.id) : undefined} onRecalculatePaths={handleRecalculatePaths} canAddChild={!!selectedSegment} canEdit={!!selectedSegment} canDelete={!!selectedSegment && !hasChildren(coaData, selectedSegment.id)} data-id="element-8958" />
            <COATreeView segments={coaData} selectedSegmentId={selectedSegment?.id} onSelectSegment={handleSelectSegment} accountTypes={accountTypes} locale={locale} data-id="element-8959" />
          </div>
          <div data-id="element-8960">
            {(selectedSegment || isEditing) && <SegmentEditor segment={showAddRoot ? null : showAddChild ? {
          ...selectedSegment,
          id: '',
          segmentCode: '',
          segmentName: '',
          parentId: selectedSegment?.id,
          level: (selectedSegment?.level || 0) + 1
        } : selectedSegment} accountTypes={accountTypes} isNew={showAddRoot || showAddChild} onSave={handleSaveSegment} onCancel={() => {
          setIsEditing(false);
          setShowAddRoot(false);
          setShowAddChild(false);
        }} locale={locale} availableOrgUnits={orgUnits} data-id="element-8961" />}
          </div>
        </div>}
      {/* Account Types View */}
      {view === 'account-types' && <AccountTypeManager accountTypes={accountTypes} onSave={handleSaveAccountType} onDelete={handleDeleteAccountType} locale={locale} data-id="element-8962" />}

      {/* OrgUnit Integration View */}
      {view === 'integration' && <OrgUnitGLIntegration orgUnits={orgUnits} coaSegments={flattenCOASegments(coaData)} data-id="element-8963" />}

      {/* OrgUnit Scope View */}
      {view === 'scope' && <COAScopeViewer coaSegments={flattenCOASegments(coaData)} orgUnits={orgUnits} data-id="element-8964" />}
    </div>;
}